import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useAppDispatch } from "../../store/hooks";
import { useLoginUserMutation } from "../../api/services/authApi";
import {
  setCredentials,
  setDefaultRoleModules,
} from "../../store/slices/authSlice";
import { setModules } from "../../store/slices/moduleSlice";
import Swal from "sweetalert2";
import { Visibility, VisibilityOff } from "@mui/icons-material";
// import { useSelector } from "react-redux";

interface LoginFormData {
  email: string;
  password: string;
}

const LoginForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();
  const [loginUser, { isLoading, error }] = useLoginUserMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // const userRoleID = useSelector((state: any) => state);

  // console.log("State functionality --> ", userRoleID);

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result: any = await loginUser({
        EMAIL: data.email,
        PASSWORD: data.password,
      });

      if (result.data) {
        const { token, defaultRoleModules, userInfo } = result.data;

        // console.log(
        //   "Login successful, storing data in Redux:",
        //   defaultRoleModules
        // );

        // Dispatch all data to Redux store (will be automatically persisted)
        dispatch(
          setCredentials({
            user: userInfo,
            token: token,
          })
        );
        dispatch(
          setDefaultRoleModules({
            moduleDetails: defaultRoleModules,
          })
        );
        dispatch(setModules(defaultRoleModules));

        const firstVisibleModule = defaultRoleModules.find(
          (module: any) => module.moduleInfo?.REVEAL === true
        );

        const redirectPath = firstVisibleModule
          ? `/${firstVisibleModule.moduleInfo.ROUTE}`
          : "/dashboard";

        navigate(redirectPath, { replace: true });
      }
    } catch (err: any) {
      console.error("Login failed:", err);

      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err?.data?.message || "Failed to Login. Please try again.",
        confirmButtonColor: "#003EB0",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
    >
      <Paper elevation={3} sx={{ padding: 4, width: 400 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Login
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Login failed. Please check your credentials.
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters",
              },
            })}
            error={!!errors.password}
            helperText={errors.password?.message}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev: any) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{ mt: 3 }}
          >
            {isLoading ? "Logning In..." : "Login"}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default LoginForm;
